import React, { useMemo } from 'react';
import Modal from '../../../components/Modal';
import { todayKST, toDateTimeString } from '../../../utils/date';
import { ib } from './classNames';

const IMPACT_LABEL = '계약일/취소일 기준으로 상황판, 직원 요약, 마케터 1:1 청구에 적용';

function formatWeight(value) {
  const n = Number(value ?? 1);
  if (!Number.isFinite(n)) return '1';
  return n.toLocaleString('ko-KR', { maximumFractionDigits: 4 });
}

function WeightSummary({ label, row, tone }) {
  const toneClass = tone === 'future'
    ? 'border-amber-200 bg-amber-50 text-amber-900 dark:border-amber-800 dark:bg-amber-950/30 dark:text-amber-100'
    : 'border-sky-200 bg-sky-50 text-sky-900 dark:border-sky-800 dark:bg-sky-950/35 dark:text-sky-100';

  return (
    <div className={`rounded-lg border px-3 py-2 ${toneClass}`}>
      <div className="text-[11px] font-extrabold opacity-75">{label}</div>
      {row ? (
        <div className="mt-1 flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
          <span className="text-lg font-black">{formatWeight(row.weight)}장</span>
          <span className="text-xs font-bold">{row.effective_from}부터</span>
        </div>
      ) : (
        <div className="mt-1 text-xs font-bold opacity-70">없음</div>
      )}
    </div>
  );
}

export function ProductWeightHistoryModal({
  ticket,
  rows,
  loading,
  saving,
  form,
  onFormChange,
  onSave,
  onClose,
}) {
  const today = todayKST();
  const { currentRow, upcomingRow, sortedRows } = useMemo(() => {
    const normalizedRows = Array.isArray(rows) ? rows : [];
    const byDesc = [...normalizedRows].sort((a, b) => {
      const dateCompare = String(b.effective_from).localeCompare(String(a.effective_from));
      return dateCompare || Number(b.id || 0) - Number(a.id || 0);
    });
    const byAsc = [...normalizedRows].sort((a, b) => {
      const dateCompare = String(a.effective_from).localeCompare(String(b.effective_from));
      return dateCompare || Number(a.id || 0) - Number(b.id || 0);
    });
    return {
      currentRow: byDesc.find((row) => String(row.effective_from) <= today) || null,
      upcomingRow: byAsc.find((row) => String(row.effective_from) > today) || null,
      sortedRows: byDesc,
    };
  }, [rows, today]);

  if (!ticket) return null;

  return (
    <Modal
      open={Boolean(ticket)}
      title={`${ticket.name} 환산장수 이력`}
      icon="📦"
      onClose={onClose}
      width="min(720px, calc(100vw - 32px))"
      mobileSheet
      footer={(
        <button type="button" className={ib('cancel', 'h-8 px-3')} onClick={onClose}>닫기</button>
      )}
    >
      <div className="grid gap-3 text-[var(--text)]">
        <div className="rounded-lg border border-[var(--border)] bg-[var(--bg-subtle)] px-3 py-2 text-[11px] font-semibold leading-relaxed text-[var(--text-sub)]">
          환산장수는 상품 이름이 아니라 장수 계산 기준입니다. {IMPACT_LABEL}됩니다.
          같은 적용 시작일로 저장하면 새 이력이 아니라 해당 날짜의 설정을 정정합니다.
        </div>

        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
          <WeightSummary label="현재 적용" row={currentRow} />
          <WeightSummary label="변경 예정" row={upcomingRow} tone="future" />
        </div>

        <div className="rounded-xl border border-[var(--border)] bg-white p-3 dark:bg-pine-900">
          <div className="mb-2 flex items-center justify-between gap-2">
            <div>
              <div className="text-sm font-black">새 적용일 저장</div>
              <div className="text-[11px] font-semibold text-[var(--text-sub)]">예: 2026-06-01부터 조식VIP(고)를 4장으로 변경</div>
            </div>
            <span className="rounded-full bg-sky-100 px-2 py-0.5 text-[10px] font-extrabold text-sky-700 dark:bg-sky-950 dark:text-sky-200">PINE</span>
          </div>
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-[140px_100px_minmax(160px,1fr)_auto] sm:items-end">
            <label className="grid gap-1 text-[11px] font-bold text-[var(--text-sub)]">
              적용 시작일
              <input
                type="date"
                value={form.effectiveFrom}
                onChange={(e) => onFormChange('effectiveFrom', e.target.value)}
                className="h-9 rounded-md border border-[var(--border)] bg-white px-2 text-sm font-bold text-[var(--text)] outline-none dark:bg-pine-800"
              />
            </label>
            <label className="grid gap-1 text-[11px] font-bold text-[var(--text-sub)]">
              환산장수
              <input
                inputMode="decimal"
                value={form.weight}
                onChange={(e) => onFormChange('weight', e.target.value.replace(/[^0-9.]/g, ''))}
                className="h-9 rounded-md border border-[var(--border)] bg-white px-2 text-right text-sm font-black text-sky-700 outline-none dark:bg-pine-800 dark:text-sky-200"
                placeholder="장수"
              />
            </label>
            <label className="grid gap-1 text-[11px] font-bold text-[var(--text-sub)]">
              변경 사유
              <input
                value={form.memo}
                onChange={(e) => onFormChange('memo', e.target.value)}
                className="h-9 rounded-md border border-[var(--border)] bg-white px-2 text-sm text-[var(--text)] outline-none dark:bg-pine-800"
                placeholder="예: 6월부터 4장 환산"
              />
            </label>
            <button type="button" className={ib('ok', 'h-9 px-3')} onClick={onSave} disabled={saving}>
              {saving ? '저장 중' : '저장'}
            </button>
          </div>
        </div>

        <div className="rounded-xl border border-[var(--border)] bg-white dark:bg-pine-900">
          <div className="flex items-center justify-between border-b border-[var(--border)] px-3 py-2">
            <div className="text-sm font-black">전체 이력</div>
            <div className="text-[11px] font-bold text-[var(--text-sub)]">{sortedRows.length}건</div>
          </div>
          {loading ? (
            <div className="px-3 py-4 text-xs font-semibold text-[var(--text-sub)]">불러오는 중...</div>
          ) : sortedRows.length === 0 ? (
            <div className="px-3 py-4 text-xs font-semibold text-[var(--text-sub)]">저장된 환산장수 이력이 없습니다.</div>
          ) : (
            <div className="divide-y divide-[var(--border)]">
              {sortedRows.map((row) => {
                const isFuture = String(row.effective_from) > today;
                const isCurrent = currentRow?.id === row.id;
                return (
                  <div key={row.id} className="grid gap-1 px-3 py-2 sm:grid-cols-[112px_82px_1fr_128px] sm:items-center">
                    <div className="flex items-center gap-1.5">
                      <span className="text-xs font-black">{row.effective_from}</span>
                      {isCurrent && <span className="rounded-full bg-sky-100 px-1.5 py-0.5 text-[9px] font-black text-sky-700 dark:bg-sky-950 dark:text-sky-200">현재</span>}
                      {isFuture && <span className="rounded-full bg-amber-100 px-1.5 py-0.5 text-[9px] font-black text-amber-700 dark:bg-amber-950 dark:text-amber-200">예정</span>}
                    </div>
                    <div className="text-sm font-black text-sky-700 dark:text-sky-200">{formatWeight(row.weight)}장</div>
                    <div className="min-w-0 text-xs font-semibold text-[var(--text-sub)]">{row.memo || '-'}</div>
                    <div className="text-[10px] font-semibold text-[var(--text-sub)] sm:text-right">
                      {row.created_by_name || '시스템'}
                      {row.created_at ? ` · ${toDateTimeString(row.created_at).slice(0, 16)}` : ''}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
}
